import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import {
  Users, Building2, Settings, FileText, Receipt, Shield, User,
  Plus, Pencil, Trash2, Lock, Unlock, Eye, EyeOff, Download,
  ChevronDown, Loader2, AlertTriangle, Check, X, Search, History, Send
} from 'lucide-react';

const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';

const TABS = [
  { id: 'profil', label: 'Mon profil', icon: User },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'clients', label: 'Clients', icon: Building2 },
  { id: 'params', label: 'Parametres', icon: Settings },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'facturation', label: 'Facturation', icon: Receipt },
  { id: 'audit', label: 'Journal d\'audit', icon: Shield },
];

const PROFIL_LABELS = {
  super_admin: { label: 'Super Admin', color: '#ef4444' },
  admin: { label: 'Admin', color: '#E8A020' },
  gestionnaire: { label: 'Gestionnaire', color: '#2D5A5A' },
  lecture: { label: 'Lecture', color: '#6366f1' },
  salarie: { label: 'Salarié', color: '#64748b' },
};

const API = process.env.REACT_APP_BACKEND_URL;

function Card({ children, className = '' }) {
  return <div className={`bg-[#1a2332] border border-white/10 rounded-xl ${className}`}>{children}</div>;
}

function Badge({ profil }) {
  const p = PROFIL_LABELS[profil] || { label: profil, color: '#666' };
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: p.color + '20', color: p.color }}>
      {p.label}
    </span>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#1a2332] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mp-admin-modal">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-white font-semibold text-sm">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const Input = (props) => (
  <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />
);
const Select = ({ children, ...props }) => (
  <select {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`}>{children}</select>
);
const Label = ({ children }) => <label className="text-slate-400 text-xs font-medium mb-1 block">{children}</label>;
const Btn = ({ children, variant = 'primary', ...props }) => {
  const cls = variant === 'primary' ? 'bg-[#2D5A5A] hover:bg-[#3a7272] text-white'
    : variant === 'danger' ? 'bg-red-600/80 hover:bg-red-600 text-white'
    : 'bg-white/5 hover:bg-white/10 text-slate-300';
  return <button {...props} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${cls} ${props.className || ''}`}>{children}</button>;
};

export default function AdministrationPage() {
  const { mpFetch, user: currentUser } = useMoanaPaye();
  const [activeTab, setActiveTab] = useState('profil');
  const [tabOpen, setTabOpen] = useState(false);
  const isSuperAdmin = currentUser?.profil === 'super_admin';

  const visibleTabs = isSuperAdmin ? TABS : TABS.filter(t => !['audit', 'facturation'].includes(t.id));

  return (
    <div data-testid="mp-administration">
      <h2 className="text-xl font-bold text-white mb-5">Administration</h2>

      <div className="mb-5">
        <div className="lg:hidden">
          <button onClick={() => setTabOpen(!tabOpen)} className="w-full flex items-center justify-between bg-[#1a2332] border border-white/10 rounded-xl px-4 py-3 text-white text-sm" data-testid="mp-admin-tab-mobile">
            <span className="flex items-center gap-2">
              {(() => { const T = visibleTabs.find(t => t.id === activeTab); return T ? <><T.icon className="w-4 h-4" />{T.label}</> : null; })()}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${tabOpen ? 'rotate-180' : ''}`} />
          </button>
          {tabOpen && (
            <div className="mt-1 bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
              {visibleTabs.map(t => (
                <button key={t.id} onClick={() => { setActiveTab(t.id); setTabOpen(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                  <t.icon className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="hidden lg:flex gap-1 bg-[#1a2332] border border-white/10 rounded-xl p-1">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`mp-admin-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'profil' && <MonProfilTab mpFetch={mpFetch} />}
      {activeTab === 'users' && <UsersTab mpFetch={mpFetch} />}
      {activeTab === 'clients' && <ClientsTab mpFetch={mpFetch} />}
      {activeTab === 'params' && <ParamsTab mpFetch={mpFetch} />}
      {activeTab === 'templates' && <TemplatesTab mpFetch={mpFetch} />}
      {activeTab === 'facturation' && <FacturationTab mpFetch={mpFetch} />}
      {activeTab === 'audit' && <AuditTab mpFetch={mpFetch} />}
    </div>
  );
}

/* =================== M7.1 USERS =================== */
function UsersTab({ mpFetch }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [entreprises, setEntreprises] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', nom: '', prenom: '', profil: 'gestionnaire', entreprise_id: '', salarie_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDroits, setShowDroits] = useState(false);
  const [droits, setDroits] = useState(null);
  const [salariesList, setSalariesList] = useState([]);

  const load = () => { mpFetch('/api/moana-paye/admin/users').then(setUsers).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { load(); mpFetch('/api/moana-paye/entreprises').then(setEntreprises).catch(console.error); }, []);

  const loadSalaries = (eid) => {
    if (eid) mpFetch(`/api/moana-paye/reporting/${eid}/salaries-list`).then(setSalariesList).catch(() => setSalariesList([]));
    else setSalariesList([]);
  };

  const handleCreate = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.salarie_id) delete payload.salarie_id;
      if (!payload.entreprise_id) delete payload.entreprise_id;
      await mpFetch('/api/moana-paye/admin/users', { method: 'POST', body: JSON.stringify(payload) });
      setShowCreate(false); setForm({ email: '', password: '', nom: '', prenom: '', profil: 'gestionnaire', entreprise_id: '', salarie_id: '' }); load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true); setError('');
    try {
      const { nom, prenom, profil, entreprise_id, actif } = editUser;
      await mpFetch(`/api/moana-paye/admin/users/${editUser.id}`, { method: 'PUT', body: JSON.stringify({ nom, prenom, profil, entreprise_id: entreprise_id || null, actif }) });
      setEditUser(null); load();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const handleDeactivate = async (uid) => {
    if (!window.confirm('Desactiver cet utilisateur ?')) return;
    try { await mpFetch(`/api/moana-paye/admin/users/${uid}`, { method: 'DELETE' }); load(); } catch (e) { alert(e.message); }
  };

  const handleUnlock = async (uid) => {
    try { await mpFetch(`/api/moana-paye/admin/users/${uid}/unlock`, { method: 'PUT', body: '{}' }); load(); } catch (e) { alert(e.message); }
  };

  const loadDroits = async () => {
    const d = await mpFetch('/api/moana-paye/admin/droits-acces');
    setDroits(d); setShowDroits(true);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-users">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-slate-400 text-xs">{users.length} utilisateur(s)</span>
        <div className="flex gap-2">
          <Btn onClick={loadDroits} variant="secondary"><Eye className="w-3.5 h-3.5" />Matrice des droits</Btn>
          <Btn onClick={() => setShowCreate(true)} data-testid="mp-admin-add-user"><Plus className="w-3.5 h-3.5" />Nouvel utilisateur</Btn>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]" data-testid="mp-users-table">
          <thead><tr className="bg-[#2D5A5A]/30 text-left">
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Nom</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Email</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Profil</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Entreprise</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Statut</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Derniere connexion</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-white/5">
            {users.map(u => (
              <tr key={u.id} className={`hover:bg-white/3 ${!u.actif ? 'opacity-50' : ''}`}>
                <td className="px-3 py-2 text-white text-xs font-medium">{u.nom} {u.prenom}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{u.email}</td>
                <td className="px-3 py-2"><Badge profil={u.profil} /></td>
                <td className="px-3 py-2 text-slate-400 text-xs">{u.entreprise_nom || '-'}</td>
                <td className="px-3 py-2">
                  {u.verrouille_jusqua ? <span className="text-xs text-red-400">Verrouille</span>
                    : u.actif ? <span className="text-xs text-green-400">Actif</span>
                    : <span className="text-xs text-slate-500">Inactif</span>}
                </td>
                <td className="px-3 py-2 text-slate-500 text-xs">{u.derniere_connexion ? new Date(u.derniere_connexion).toLocaleString('fr-FR') : '-'}</td>
                <td className="px-3 py-2 flex gap-1">
                  <button onClick={() => { setEditUser({...u}); setError(''); }} className="text-slate-400 hover:text-white" title="Modifier"><Pencil className="w-3.5 h-3.5" /></button>
                  {u.verrouille_jusqua && <button onClick={() => handleUnlock(u.id)} className="text-[#E8A020] hover:text-white" title="Deverrouiller"><Unlock className="w-3.5 h-3.5" /></button>}
                  {u.profil !== 'super_admin' && u.actif && <button onClick={() => handleDeactivate(u.id)} className="text-red-400 hover:text-red-300" title="Desactiver"><Trash2 className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nouvel utilisateur">
        {error && <div className="text-red-400 text-xs mb-3 bg-red-500/10 p-2 rounded-lg">{error}</div>}
        <div className="space-y-3">
          <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="mp-user-email" /></div>
          <div><Label>Mot de passe (12 car, maj, chiffre, special)</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="mp-user-password" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nom</Label><Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} /></div>
            <div><Label>Prénom</Label><Input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} /></div>
          </div>
          <div><Label>Profil</Label>
            <Select value={form.profil} onChange={e => setForm({ ...form, profil: e.target.value })} data-testid="mp-user-profil">
              <option value="admin">Admin Moana Paye</option>
              <option value="gestionnaire">Gestionnaire client</option>
              <option value="lecture">Lecture seule</option>
              <option value="salarie">Salarié</option>
            </Select>
          </div>
          <div><Label>Entreprise</Label>
            <Select value={form.entreprise_id} onChange={e => { setForm({ ...form, entreprise_id: e.target.value }); loadSalaries(e.target.value); }}>
              <option value="">— Aucune (admin global) —</option>
              {entreprises.map(e => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
            </Select>
          </div>
          {form.profil === 'salarie' && form.entreprise_id && (
            <div><Label>Fiche salarié liée</Label>
              <Select value={form.salarie_id} onChange={e => setForm({ ...form, salarie_id: e.target.value })} data-testid="mp-user-salarie-id">
                <option value="">— Sélectionner le salarié —</option>
                {salariesList.map(s => <option key={s.id} value={s.id}>{s.nom} {s.prenom} ({s.statut})</option>)}
              </Select>
            </div>
          )}
          <Btn onClick={handleCreate} disabled={saving} className="w-full justify-center" data-testid="mp-user-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Créer l'utilisateur
          </Btn>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Modifier utilisateur">
        {editUser && (
          <div className="space-y-3">
            {error && <div className="text-red-400 text-xs mb-3 bg-red-500/10 p-2 rounded-lg">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom</Label><Input value={editUser.nom} onChange={e => setEditUser({ ...editUser, nom: e.target.value })} /></div>
              <div><Label>Prénom</Label><Input value={editUser.prenom} onChange={e => setEditUser({ ...editUser, prenom: e.target.value })} /></div>
            </div>
            <div><Label>Profil</Label>
              <Select value={editUser.profil} onChange={e => setEditUser({ ...editUser, profil: e.target.value })} disabled={editUser.profil === 'super_admin'}>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin Moana Paye</option>
                <option value="gestionnaire">Gestionnaire client</option>
                <option value="lecture">Lecture seule</option>
                <option value="salarie">Salarié</option>
              </Select>
            </div>
            <div><Label>Entreprise</Label>
              <Select value={editUser.entreprise_id || ''} onChange={e => setEditUser({ ...editUser, entreprise_id: e.target.value || null })}>
                <option value="">— Aucune —</option>
                {entreprises.map(e => <option key={e.id} value={e.id}>{e.raison_sociale}</option>)}
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editUser.actif} onChange={e => setEditUser({ ...editUser, actif: e.target.checked })} className="rounded" />
              <span className="text-slate-400 text-xs">Compte actif</span>
            </div>
            <Btn onClick={handleUpdate} disabled={saving} className="w-full justify-center" data-testid="mp-user-update">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Enregistrer
            </Btn>
          </div>
        )}
      </Modal>

      {/* Droits modal */}
      <Modal open={showDroits} onClose={() => setShowDroits(false)} title="Matrice des droits d'accès">
        {droits && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="mp-droits-table">
              <thead><tr className="bg-[#2D5A5A]/30">
                <th className="px-2 py-1.5 text-slate-300 font-medium text-left">Module</th>
                {droits.profils.map(p => <th key={p} className="px-2 py-1.5 text-center"><Badge profil={p} /></th>)}
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {Object.entries(droits.matrice).map(([mod, rights]) => (
                  <tr key={mod} className="hover:bg-white/3">
                    <td className="px-2 py-1.5 text-white font-medium">{mod}</td>
                    {droits.profils.map(p => (
                      <td key={p} className="px-2 py-1.5 text-center">
                        <span className={`${rights[p] === 'rw' ? 'text-green-400' : rights[p] === '-' ? 'text-slate-600' : 'text-[#E8A020]'}`}>
                          {rights[p] === 'rw' ? 'R/W' : rights[p] === '-' ? '—' : rights[p]}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* =================== M7.2 CLIENTS =================== */
function ClientsTab({ mpFetch }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editClient, setEditClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const load = () => { mpFetch('/api/moana-paye/admin/clients').then(setClients).catch(console.error).finally(() => setLoading(false)); };
  useEffect(load, []);

  const statusColor = (s) => s === 'actif' ? 'text-green-400 bg-green-500/15' : s === 'suspendu' ? 'text-[#E8A020] bg-[#E8A020]/15' : 'text-red-400 bg-red-500/15';
  const modeLabel = (m) => m === 'autonome' ? 'Autonome' : m === 'semi' ? 'Semi-délégation' : m === 'delegation' ? 'Délégation' : m || '-';

  const handleAction = async (eid, action) => {
    if (!window.confirm(`Confirmer : ${action} ?`)) return;
    try {
      await mpFetch(`/api/moana-paye/admin/clients/${eid}/${action}`, { method: 'PUT', body: '{}' });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { statut_client, gestionnaire_referent, periodicite_cst, mode_utilisation, maj_params_autorisee, notes_internes, formule, identifiant_fiscal, mode_bulletin } = editClient;
      await mpFetch(`/api/moana-paye/admin/clients/${editClient.id}`, {
        method: 'PUT', body: JSON.stringify({ statut_client, gestionnaire_referent, periodicite_cst, mode_utilisation, maj_params_autorisee, notes_internes, formule, identifiant_fiscal, mode_bulletin })
      });
      setEditClient(null); load();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-clients">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-slate-400 text-xs">{clients.length} entreprise(s) cliente(s)</span>
        <Btn onClick={() => setShowOnboarding(!showOnboarding)} data-testid="btn-onboarding">
          <Plus className="w-3.5 h-3.5" />{showOnboarding ? 'Fermer' : 'Onboarder un client'}
        </Btn>
      </div>

      {showOnboarding && <div className="mb-5"><OnboardingForm mpFetch={mpFetch} onDone={() => { setShowOnboarding(false); load(); }} /></div>}

      <div className="space-y-3">
        {clients.map(c => (
          <Card key={c.id} className="p-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-semibold text-sm">{c.raison_sociale}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(c.statut_client || 'actif')}`}>{c.statut_client || 'actif'}</span>
                  {c.formule && <span className="text-xs px-2 py-0.5 rounded-full bg-[#2D5A5A]/15 text-[#2D5A5A]">{c.formule === 'delegation' ? 'Délégation' : c.formule === 'semi' ? 'Semi-délég.' : 'Autonomie'}</span>}
                </div>
                <div className="text-slate-500 text-xs space-x-4">
                  <span>N. Tahiti: {c.numero_tahiti || '-'}</span>
                  <span>Mode: {modeLabel(c.mode_utilisation)}</span>
                  <span>{c.nb_salaries} sal.</span>
                  <span>{c.nb_users} user(s)</span>
                  {c.formule && <span>Formule: {c.formule}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Btn onClick={() => setEditClient({...c})} variant="secondary"><Pencil className="w-3 h-3" />Modifier</Btn>
                {(c.statut_client || 'actif') === 'actif' && <Btn onClick={() => handleAction(c.id, 'suspendre')} variant="secondary"><Lock className="w-3 h-3" /></Btn>}
                {c.statut_client === 'suspendu' && <Btn onClick={() => handleAction(c.id, 'reactiver')}><Unlock className="w-3 h-3" /></Btn>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!editClient} onClose={() => setEditClient(null)} title="Fiche client Moana Paye">
        {editClient && (
          <div className="space-y-3">
            <div className="text-white font-semibold text-sm mb-2">{editClient.raison_sociale}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Identifiant fiscal</Label><Input value={editClient.identifiant_fiscal || ''} onChange={e => setEditClient({ ...editClient, identifiant_fiscal: e.target.value })} /></div>
              <div><Label>Formule souscrite</Label>
                <Select value={editClient.formule || 'delegation'} onChange={e => setEditClient({ ...editClient, formule: e.target.value })} data-testid="mp-client-formule">
                  <option value="delegation">Délégation totale</option>
                  <option value="semi">Semi-délégation</option>
                  <option value="autonome">Autonomie totale</option>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mode utilisation</Label>
                <Select value={editClient.mode_utilisation || 'autonome'} onChange={e => setEditClient({ ...editClient, mode_utilisation: e.target.value })}>
                  <option value="autonome">Autonome</option><option value="semi">Semi-délégation</option><option value="delegation">Délégation</option>
                </Select>
              </div>
              <div><Label>Périodicité CST</Label>
                <Select value={editClient.periodicite_cst || 'mensuelle'} onChange={e => setEditClient({ ...editClient, periodicite_cst: e.target.value })}>
                  <option value="mensuelle">Mensuelle</option><option value="trimestrielle">Trimestrielle</option>
                </Select>
              </div>
            </div>
            <div><Label>Gestionnaire référent</Label><Input value={editClient.gestionnaire_referent || ''} onChange={e => setEditClient({ ...editClient, gestionnaire_referent: e.target.value })} /></div>
            <div><Label>Mode bulletin</Label><Input value={editClient.mode_bulletin || ''} onChange={e => setEditClient({ ...editClient, mode_bulletin: e.target.value })} placeholder="Charte client (logo + couleurs)" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={editClient.maj_params_autorisee || false} onChange={e => setEditClient({ ...editClient, maj_params_autorisee: e.target.checked })} />
              <span className="text-slate-400 text-xs">MAJ paramètres CPS/CST autorisée</span>
            </div>
            <div><Label>Notes internes (admin only)</Label>
              <textarea value={editClient.notes_internes || ''} onChange={e => setEditClient({ ...editClient, notes_internes: e.target.value })}
                className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm h-20 focus:outline-none focus:border-[#2D5A5A]" />
            </div>
            <Btn onClick={handleSave} disabled={saving} className="w-full justify-center" data-testid="mp-client-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Enregistrer
            </Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* =================== M7.3 PARAMS =================== */
function ParamsTab({ mpFetch }) {
  const [params, setParams] = useState(null);
  useEffect(() => { mpFetch('/api/moana-paye/admin/parametres-systeme').then(setParams).catch(console.error); }, []);
  if (!params) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-params">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-white font-semibold text-sm mb-3">Informations generales</h3>
          <div className="space-y-2 text-xs">
            {[['Logiciel', params.logiciel], ['Editeur', params.editeur], ['Version', params.version],
              ['Fuseau horaire', params.fuseau_horaire], ['Langue', params.langue], ['Devise', params.devise]
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-white/5">
                <span className="text-slate-400">{k}</span><span className="text-white font-medium">{v}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <h3 className="text-white font-semibold text-sm mb-3">Statistiques</h3>
          <div className="grid grid-cols-2 gap-3">
            {[['Entreprises', params.stats.nb_entreprises, Building2, '#2D5A5A'],
              ['Salaries', params.stats.nb_salaries, Users, '#E8A020'],
              ['Utilisateurs', params.stats.nb_utilisateurs, Users, '#6366f1'],
              ['Jours fériés', params.stats.nb_jours_feries, Settings, '#22c55e'],
              ['Cot. CPS', params.stats.nb_cotisations_cps, Shield, '#2D5A5A'],
              ['Tranches CST', params.stats.nb_tranches_cst, Shield, '#E8A020'],
            ].map(([label, val, Icon, color]) => (
              <div key={label} className="bg-white/5 rounded-lg p-3 text-center">
                <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} />
                <div className="text-white font-bold text-lg">{val}</div>
                <div className="text-slate-500 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4 lg:col-span-2">
          <h3 className="text-white font-semibold text-sm mb-3">Securite</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
            {Object.entries(params.securite).map(([k, v]) => (
              <div key={k} className="bg-white/5 rounded-lg p-3">
                <div className="text-slate-400 mb-1">{k.replace(/_/g, ' ')}</div>
                <div className="text-white font-medium">{v}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* =================== M7.4 TEMPLATES =================== */
function TemplatesTab({ mpFetch }) {
  const { token } = useMoanaPaye();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { mpFetch('/api/moana-paye/admin/templates').then(setTemplates).catch(console.error).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleUpload = async (tid, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('version', new Date().getFullYear().toString());
    const res = await fetch(`${API}/api/moana-paye/admin/templates/${tid}/upload`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }, body: formData
    });
    const data = await res.json();
    if (!res.ok) { alert(data.detail || 'Erreur'); return; }
    alert(data.message); load();
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-templates">
      <p className="text-slate-500 text-xs mb-4">Templates PDF officiels utilisés pour la génération des déclarations sociales. Seul le super administrateur peut les remplacer.</p>
      <div className="space-y-3">
        {templates.map(t => (
          <Card key={t.id} className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-white font-medium text-sm">{t.libelle}</div>
              <div className="text-slate-500 text-xs">Organisme: {t.organisme} — Version: {t.version || '-'}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${t.actif ? 'bg-green-500/15 text-green-400' : 'bg-slate-500/15 text-slate-400'}`}>
                {t.actif ? 'Actif' : 'Inactif'}
              </span>
              <label className="cursor-pointer">
                <input type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleUpload(t.id, e.target.files[0]); }} />
                <span className="inline-flex items-center gap-1 bg-[#E8A020] hover:bg-[#d4911a] text-[#1a2332] text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer">
                  <FileText className="w-3 h-3" />Remplacer PDF
                </span>
              </label>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* =================== M7.5 FACTURATION =================== */
function FacturationTab({ mpFetch }) {
  const [data, setData] = useState(null);
  useEffect(() => { mpFetch('/api/moana-paye/admin/facturation').then(setData).catch(console.error); }, []);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-facturation">
      <p className="text-slate-500 text-xs mb-4">Donnees de facturation pour EBP Gescom — {data.label}</p>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]" data-testid="mp-facturation-table">
          <thead><tr className="bg-[#2D5A5A]/30 text-left">
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Client</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Mode</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Salaries</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Bulletins mois</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Declarations mois</th>
            <th className="px-3 py-2 text-slate-300 font-medium text-xs">Statut</th>
          </tr></thead>
          <tbody className="divide-y divide-white/5">
            {data.clients.map(c => (
              <tr key={c.id} className="hover:bg-white/3">
                <td className="px-3 py-2 text-white text-xs font-medium">{c.raison_sociale}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{c.mode_utilisation || '-'}</td>
                <td className="px-3 py-2 text-white text-xs text-right">{c.nb_salaries}</td>
                <td className="px-3 py-2 text-[#E8A020] text-xs text-right font-medium">{c.nb_bulletins_mois}</td>
                <td className="px-3 py-2 text-slate-300 text-xs text-right">{c.nb_declarations_mois}</td>
                <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${c.statut_client === 'actif' || !c.statut_client ? 'bg-green-500/15 text-green-400' : 'bg-[#E8A020]/15 text-[#E8A020]'}`}>{c.statut_client || 'actif'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* =================== M7.6 AUDIT =================== */
function AuditTab({ mpFetch }) {
  const { token } = useMoanaPaye();
  const [logs, setLogs] = useState({ logs: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ categorie: '', utilisateur: '', date_debut: '', date_fin: '' });
  const [page, setPage] = useState(1);

  const load = async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 30 });
    if (filters.categorie) params.append('categorie', filters.categorie);
    if (filters.utilisateur) params.append('utilisateur', filters.utilisateur);
    if (filters.date_debut) params.append('date_debut', filters.date_debut);
    if (filters.date_fin) params.append('date_fin', filters.date_fin);
    try {
      const d = await mpFetch(`/api/moana-paye/admin/audit-logs?${params}`);
      setLogs(d); setPage(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); mpFetch('/api/moana-paye/admin/audit-logs/categories').then(setCategories).catch(() => {}); }, []);

  const selectCls = "bg-[#0f1520] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#2D5A5A]";

  return (
    <div data-testid="mp-admin-audit">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={filters.categorie} onChange={e => setFilters({ ...filters, categorie: e.target.value })} className={selectCls}>
          <option value="">Toutes categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Utilisateur..." value={filters.utilisateur} onChange={e => setFilters({ ...filters, utilisateur: e.target.value })} className={selectCls + ' w-32'} />
        <input type="date" value={filters.date_debut} onChange={e => setFilters({ ...filters, date_debut: e.target.value })} className={selectCls} />
        <input type="date" value={filters.date_fin} onChange={e => setFilters({ ...filters, date_fin: e.target.value })} className={selectCls} />
        <Btn onClick={() => load(1)} data-testid="mp-audit-search"><Search className="w-3 h-3" />Rechercher</Btn>
        <a href={`${API}/api/moana-paye/admin/audit-logs/export/pdf?${new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)))}`}
           target="_blank" rel="noreferrer">
          <Btn variant="secondary"><Download className="w-3 h-3" />PDF</Btn>
        </a>
      </div>

      <span className="text-slate-500 text-xs mb-2 block">{logs.total} evenement(s) — Page {logs.page}/{logs.pages || 1}</span>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]" data-testid="mp-audit-table">
            <thead><tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Date/Heure</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Categorie</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Action</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Detail</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Utilisateur</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">IP</th>
            </tr></thead>
            <tbody className="divide-y divide-white/5">
              {logs.logs.map(l => (
                <tr key={l.id} className="hover:bg-white/3">
                  <td className="px-3 py-2 text-slate-400 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString('fr-FR')}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-[#2D5A5A]/20 text-[#2D5A5A]">{l.categorie}</span></td>
                  <td className="px-3 py-2 text-white text-xs">{l.action}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs max-w-[200px] truncate" title={l.detail}>{l.detail || '-'}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{l.utilisateur_email || '-'}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{l.adresse_ip || '-'}</td>
                </tr>
              ))}
              {logs.logs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-xs">Aucun evenement trouve</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {logs.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Btn onClick={() => load(page - 1)} disabled={page <= 1} variant="secondary">Prec.</Btn>
          <span className="text-slate-400 text-xs">{page} / {logs.pages}</span>
          <Btn onClick={() => load(page + 1)} disabled={page >= logs.pages} variant="secondary">Suiv.</Btn>
        </div>
      )}
    </div>
  );
}


/* =================== MON PROFIL =================== */
function MonProfilTab({ mpFetch }) {
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwForm, setPwForm] = useState({ ancien: '', nouveau: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  useEffect(() => {
    mpFetch('/api/moana-paye/admin/mon-profil').then(setProfil).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handlePwChange = async () => {
    setPwErr(''); setPwMsg('');
    if (pwForm.nouveau !== pwForm.confirm) { setPwErr('Les mots de passe ne correspondent pas'); return; }
    setPwSaving(true);
    try {
      const res = await mpFetch('/api/moana-paye/admin/mon-profil/password', {
        method: 'PUT', body: JSON.stringify({ ancien: pwForm.ancien, nouveau: pwForm.nouveau })
      });
      setPwMsg(res.message);
      setPwForm({ ancien: '', nouveau: '', confirm: '' });
    } catch (e) { setPwErr(e.message); }
    setPwSaving(false);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-admin-profil" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><User className="w-4 h-4 text-[#2D5A5A]" />Mon compte</h3>
        {profil && (
          <div className="space-y-2 text-xs">
            {[['Nom', `${profil.nom} ${profil.prenom}`], ['Email', profil.email], ['Profil', profil.profil],
              ['Derniere connexion', profil.derniere_connexion ? new Date(profil.derniere_connexion).toLocaleString('fr-FR') : '-'],
              ['Compte créé le', profil.created_at ? new Date(profil.created_at).toLocaleString('fr-FR') : '-']
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b border-white/5">
                <span className="text-slate-400">{k}</span><span className="text-white font-medium">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><Lock className="w-4 h-4 text-[#E8A020]" />Modifier mon mot de passe</h3>
        {pwMsg && <div className="text-green-400 text-xs mb-3 bg-green-500/10 p-2 rounded-lg" data-testid="pw-success">{pwMsg}</div>}
        {pwErr && <div className="text-red-400 text-xs mb-3 bg-red-500/10 p-2 rounded-lg" data-testid="pw-error">{pwErr}</div>}
        <div className="space-y-3">
          <div><Label>Ancien mot de passe</Label><Input type="password" value={pwForm.ancien} onChange={e => setPwForm({ ...pwForm, ancien: e.target.value })} data-testid="pw-ancien" /></div>
          <div><Label>Nouveau mot de passe</Label><Input type="password" value={pwForm.nouveau} onChange={e => setPwForm({ ...pwForm, nouveau: e.target.value })} data-testid="pw-nouveau" /></div>
          <div><Label>Confirmer</Label><Input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          <p className="text-slate-500 text-xs">12 caracteres min, 1 majuscule, 1 chiffre, 1 caractere special</p>
          <Btn onClick={handlePwChange} disabled={pwSaving || !pwForm.ancien || !pwForm.nouveau || !pwForm.confirm} className="w-full justify-center" data-testid="pw-submit">
            {pwSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Modifier le mot de passe
          </Btn>
        </div>
      </Card>
    </div>
  );
}

/* =================== ONBOARDING CLIENT (inside ClientsTab) =================== */
function OnboardingForm({ mpFetch, onDone }) {
  const [form, setForm] = useState({
    raison_sociale: '', numero_tahiti: '', forme_juridique: '', ville: 'Papeete', ile: 'Tahiti',
    formule: 'delegation', contact_nom: '', contact_prenom: '', contact_email: '', contact_profil: 'gestionnaire'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    setSaving(true); setError(''); setResult(null);
    try {
      const res = await mpFetch('/api/moana-paye/admin/onboarding', { method: 'POST', body: JSON.stringify(form) });
      setResult(res);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  if (result) {
    return (
      <Card className="p-5 border-green-500/30" data-testid="onboarding-result">
        <h4 className="text-green-400 font-semibold text-sm mb-3 flex items-center gap-2"><Check className="w-4 h-4" />Client onboarde avec succes</h4>
        <div className="space-y-2 text-xs mb-4">
          <div className="flex justify-between py-1"><span className="text-slate-400">Entreprise</span><span className="text-white">{form.raison_sociale}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-400">Email</span><span className="text-white">{result.email}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-400">Mot de passe temporaire</span><span className="text-white font-mono bg-white/10 px-2 py-0.5 rounded">{result.temp_password}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-400">Formule</span><span className="text-[#2D5A5A] font-medium">{result.formule === 'delegation' ? 'Délégation totale' : result.formule === 'semi' ? 'Semi-délégation' : 'Autonomie totale'}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-400">Email envoye</span><span className={result.email_sent ? 'text-green-400' : 'text-[#E8A020]'}>{result.email_sent ? 'Oui' : 'Non (envoi manuel requis)'}</span></div>
        </div>
        <Btn onClick={() => { setResult(null); onDone(); }} className="w-full justify-center">Fermer</Btn>
      </Card>
    );
  }

  return (
    <Card className="p-5 border-[#E8A020]/30" data-testid="onboarding-form">
      <h4 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#E8A020]" />Onboarder un nouveau client</h4>
      {error && <div className="text-red-400 text-xs mb-3 bg-red-500/10 p-2 rounded-lg">{error}</div>}

      <p className="text-slate-500 text-xs mb-4">Cree l'entreprise, le compte utilisateur et envoie les identifiants par email.</p>

      <div className="space-y-3">
        <div className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-1">Entreprise</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Raison sociale *</Label><Input value={form.raison_sociale} onChange={e => setForm({ ...form, raison_sociale: e.target.value })} data-testid="onb-rs" /></div>
          <div><Label>N. Tahiti</Label><Input value={form.numero_tahiti} onChange={e => setForm({ ...form, numero_tahiti: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Forme juridique</Label><Input value={form.forme_juridique} onChange={e => setForm({ ...form, forme_juridique: e.target.value })} placeholder="SARL, SAS..." /></div>
          <div><Label>Ville</Label><Input value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} /></div>
          <div><Label>Ile</Label><Input value={form.ile} onChange={e => setForm({ ...form, ile: e.target.value })} /></div>
        </div>
        <div>
          <Label>Formule souscrite *</Label>
          <Select value={form.formule} onChange={e => setForm({ ...form, formule: e.target.value })} data-testid="onb-formule">
            <option value="delegation">Délégation totale — AERH gère tout</option>
            <option value="semi">Semi-délégation — Client saisit, AERH valide</option>
            <option value="autonome">Autonomie totale — Client gère avec Moana Paye</option>
          </Select>
        </div>

        <div className="text-slate-300 text-xs font-semibold uppercase tracking-wider mb-1 mt-4">Contact principal</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nom *</Label><Input value={form.contact_nom} onChange={e => setForm({ ...form, contact_nom: e.target.value })} data-testid="onb-nom" /></div>
          <div><Label>Prénom *</Label><Input value={form.contact_prenom} onChange={e => setForm({ ...form, contact_prenom: e.target.value })} data-testid="onb-prenom" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Email *</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} data-testid="onb-email" /></div>
          <div><Label>Profil</Label>
            <Select value={form.contact_profil} onChange={e => setForm({ ...form, contact_profil: e.target.value })}>
              <option value="gestionnaire">Gestionnaire</option>
              <option value="lecture">Lecture seule</option>
            </Select>
          </div>
        </div>

        <Btn onClick={handleSubmit} disabled={saving || !form.raison_sociale || !form.contact_nom || !form.contact_email}
          className="w-full justify-center" data-testid="onb-submit">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Créer le compte et envoyer les identifiants
        </Btn>
      </div>
    </Card>
  );
}
